// Detalle de un módulo: cabecera + lista de lecciones del módulo.

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { Header } from '@/components/organisms/Header';
import { ApiError } from '@/services/api';
import { getModule, type LessonListItem, type ModuleListItem } from '@/services/lessons';
import styles from './Lecciones.module.scss';

export function ModuleDetail() {
  const { id: idStr } = useParams<{ id: string }>();
  const [mod, setMod] = useState<ModuleListItem | null>(null);
  const [lessons, setLessons] = useState<LessonListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!idStr) return;
    const id = Number(idStr);
    if (!Number.isInteger(id) || id < 1) { setError('Id inválido'); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    getModule(id)
      .then((r) => { if (!cancelled) { setMod(r.module); setLessons(r.lessons); } })
      .catch((e) => !cancelled && setError(e instanceof ApiError ? e.message : 'Error'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, [idStr]);

  return (
    <>
      <Header title={mod?.title ?? 'Módulo'} />
      <main className={styles.main}>
        <Link to="/lecciones" className={styles.backLink}>
          <Icon name="chevron-left" size={16} /> Volver a módulos
        </Link>

        {loading ? (
          <div className={styles.center}><Spinner size="lg" /></div>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : !mod ? null : (
          <>
            <header className={styles.moduleHeader}>
              <div className={styles.moduleMeta}>
                <Badge tone="accent">{mod.level_label}</Badge>
                {mod.year && <span className={styles.muted}>{mod.year}</span>}
                {mod.code && <span className={styles.muted}>· {mod.code}</span>}
              </div>
              <h2 className={styles.moduleTitle}>{mod.title}</h2>
            </header>

            {lessons.length === 0 ? (
              <p className={styles.muted}>Este módulo aún no tiene lecciones publicadas.</p>
            ) : (
              <ol className={styles.lessonList}>
                {lessons.map((l, i) => (
                  <li key={l.id}>
                    <Link to={`/lecciones/leccion/${l.id}`} className={styles.lessonRow}>
                      <span className={styles.lessonNumber}>{String(i + 1).padStart(2, '0')}</span>
                      {l.card_image_url ? (
                        <img src={l.card_image_url} alt="" className={styles.lessonThumb} />
                      ) : (
                        <span className={styles.lessonThumb}><Icon name="book" size={20} /></span>
                      )}
                      <div className={styles.lessonBody}>
                        <h4 className={styles.lessonTitle}>{l.title}</h4>
                        {l.overview && <p className={styles.lessonOverview}>{l.overview}</p>}
                      </div>
                      <Icon name="chevron-right" size={18} />
                    </Link>
                  </li>
                ))}
              </ol>
            )}
          </>
        )}
      </main>
    </>
  );
}
